// BulkSalesforceRefreshDialog.tsx
// Triggered from the Sites list multi-select toolbar: refreshes every selected
// site from Salesforce (server/features/salesforce/routes/salesforce.ts,
// POST /sync-site/bulk). Runs to completion within one request/response —
// no run persisted server-side, just the per-site result list shown below.

import { useState } from 'react';
import { Loader2, CheckCircle2, XCircle, PlayCircle, ExternalLink } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../../foundation/altzero_base/base/components/ui/dialog';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { salesforceService, type SfBulkRefreshResult } from '../../services/salesforceService';
import type { BvSite } from '../../types/billValidationTypes';

type Props = {
  open: boolean;
  onClose: () => void;
  sites: BvSite[];
};

export function BulkSalesforceRefreshDialog({ open, onClose, sites }: Props) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SfBulkRefreshResult[] | null>(null);

  const handleStart = async () => {
    setRunning(true);
    setError(null);
    setResults(null);
    const res = await salesforceService.bulkRefresh(sites.map((s) => s.id));
    setRunning(false);
    if (!res.success || !res.data) {
      setError(res.error ?? 'Failed to run bulk refresh');
      return;
    }
    setResults(res.data.results);
  };

  const openSite = (siteId: string) => {
    window.open(`/bill-validation/sites/${siteId}`, '_blank', 'noopener,noreferrer');
  };

  const siteById = new Map(sites.map((s) => [s.id, s]));
  const doneCount = results?.filter((r) => r.success).length ?? 0;
  const errorCount = results?.filter((r) => !r.success).length ?? 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); setResults(null); } }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Salesforce Refresh — {sites.length} site{sites.length === 1 ? '' : 's'}</DialogTitle>
        </DialogHeader>

        {!results ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Refreshes site, DMA and metering-contact fields from each site's linked Salesforce Opportunity.
              Sites with no linked Opportunity (no <code>sf_site_id</code>) will show as failed below.
            </p>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="gap-1.5" disabled={running || sites.length === 0} onClick={() => void handleStart()}>
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                {running ? 'Refreshing…' : 'Start Bulk Refresh'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg border border-gray-100 px-3 py-2">
                <p className="text-xs text-gray-400">Sites</p>
                <p className="text-lg font-semibold text-gray-900">{results.length}</p>
              </div>
              <div className="rounded-lg border border-gray-100 px-3 py-2">
                <p className="text-xs text-gray-400">Refreshed</p>
                <p className="text-lg font-semibold text-green-700">{doneCount}</p>
              </div>
              <div className="rounded-lg border border-gray-100 px-3 py-2">
                <p className="text-xs text-gray-400">Errors</p>
                <p className="text-lg font-semibold text-red-700">{errorCount}</p>
              </div>
            </div>

            <div className="rounded-lg border border-gray-100 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="py-2 px-3 text-left font-medium text-gray-500">NMI / Site</th>
                    <th className="py-2 px-3 text-left font-medium text-gray-500">Result</th>
                    <th className="py-2 px-3 text-right font-medium text-gray-500"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {results.map((r) => {
                    const site = siteById.get(r.site_id);
                    return (
                      <tr key={r.site_id} className="hover:bg-gray-50">
                        <td className="py-2 px-3">
                          <span className="font-mono text-gray-700">{r.nmi ?? site?.nmi ?? '—'}</span>
                          <span className="block text-gray-400">{site?.client?.name ?? ''}</span>
                        </td>
                        <td className="py-2 px-3">
                          {r.success
                            ? <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="h-3.5 w-3.5" /> Refreshed</span>
                            : <span className="inline-flex items-center gap-1 text-red-700" title={r.error}><XCircle className="h-3.5 w-3.5" /> {r.error ?? 'Failed'}</span>}
                        </td>
                        <td className="py-2 px-3 text-right">
                          {!r.success && (
                            <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => openSite(r.site_id)}>
                              Open <ExternalLink className="h-3 w-3" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end pt-2 border-t border-gray-100">
              <Button variant="outline" onClick={() => { onClose(); setResults(null); }}>Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
